/****************************************************
 * Module Name:  LotSplitModal.js
 * Project:      Pansuriya Impex Sales & Manufacturing
 * Owner:        info@pansuriyaimpex.com
 *
 * Copyright (c) Pansuriya Impex
 * All rights reserved.
 ****************************************************/
import { Box, Button, CircularProgress, Tooltip } from "@mui/material";
import { DataGridPro, GridToolbar } from "@mui/x-data-grid-pro";
import React, { useState } from "react";
import { Modal } from "react-bootstrap";
import CommonSnackbar from "../../common/commonSnackBar";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import { LoadingButton } from "@mui/lab";
import { BackupTable } from "@mui/icons-material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import renderCellExpand from "../../common/dataGridCellExpand";
import Papa from 'papaparse';
import Constants from "../../../utility/constants";
import logo from '../../../images/pansuriya.jpg';
import { MdCallSplit } from "react-icons/md";

const LotSplitModal = ({ rowData, handleClose, loading, splitLot }) => {

    const [data, setData] = useState([]);
    const [totalCarat, setTotalCarat] = useState(0.00);
    const [totalPcs, setTotalPcs] = useState(0.00);
    const [showSnackbar, setShowSnackbar] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState("");
    const [snackbarVariant, setSnackbarVariant] = useState("");
    const [uploadLoader, setUploadLoader] = useState(false);

    const setSnackbar = (showSnackbar, snackbarMessage, snackbarVariant) => {
        setShowSnackbar(showSnackbar);
        setSnackbarMessage(snackbarMessage);
        setSnackbarVariant(snackbarVariant);
    }

    const splitLotIntoMultiple = () => {

        if (totalCarat === rowData.pendingcarats || totalPcs === rowData.pendingpcs) {
            if (splitLot) {
                splitLot(data);
            }
        } else {
            setSnackbar(true, "Total Carat is not same as the Lot Carat OR Total Pcs is not same as the Lot Pcs", "warning");
        }
    }

    const handleUpload = (file) => {
        setUploadLoader(true);
        const reader = new FileReader();
        reader.onload = () => {
            const text = reader.result;
            Papa.parse(text, {
                header: true,
                skipEmptyLines: true,
                complete: (result) => {
                    if (result.data.length <= 0) {
                        setSnackbar(true, "No Packets Found", "warning");
                        return;
                    }

                    let headersFromCSV = Object.keys(result.data[0]);
                    let requiredColumns = ['Sr. No.', 'Rough Company', 'Rough Contry', 'Source', 'Tender',
                        'Month', 'Box', 'Rough Quality', 'Rough Size', 'Pcs', 'Carat', 'Avg. Size',
                        'Price', 'Gross Amount', 'Charge Price', 'Total Price', 'Total Amount', 'Remark'];

                    if (headersFromCSV.length === requiredColumns.length) {
                        let totalCarat = 0, totalPcs = 0;
                        let newData = result.data.filter(iData => iData['Sr. No.'] !== "" && iData['Rough Company'] !== "" &&
                            iData['Rough Contry'] && iData['Source'] && iData['Tender'])
                            .map((iData, index) => {
                                totalCarat += iData['Carat'] ? parseFloat(iData['Carat']) : 0;
                                totalPcs += iData['Pcs'] ? parseInt(iData['Pcs']) : 0;
                                return {
                                    id: index + 1,
                                    lotid: rowData.lotcreationid,
                                    roughquality: iData['Rough Quality'],
                                    roughsize: iData['Rough Size'],
                                    pcs: iData['Pcs'],
                                    carat: iData['Carat'],
                                    avgsize: iData['Avg. Size'],
                                    grossprice: iData['Price'],
                                    grosstotal: iData['Gross Amount'],
                                    chargeprice: iData['Charge Price'],
                                    price: iData['Total Price'],
                                    total: iData['Total Amount'],
                                    remark: iData['Remark']
                                }
                            })

                        setTotalCarat(totalCarat);
                        setTotalPcs(totalPcs);
                        setData(newData);
                        setUploadLoader(false);
                        setSnackbar(true, "Uploaded Successfully", "success");
                    } else {
                        console.error('CSV columns do not match table columns.');
                        setUploadLoader(false);
                        setSnackbar(true, "CSV columns do not match table columns.", "error");
                    }
                },
                error: (error) => {
                    console.error('Error parsing CSV:', error);
                    setUploadLoader(false);
                    setSnackbar(true, `'Error parsing CSV:', ${error}`, "error");
                }
            });
        };

        reader.readAsText(file);
    };

    const handleClear = () => {
        const fileInput = document.getElementById('file-input');
        fileInput.value = null;
    }

    const initialColumns = () => [
        {
            field: 'id',
            headerName: 'SR. NO.',
            headerAlign: 'center',
            align: 'center',
            type: 'number',
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
            width: 70
        },
        {
            field: 'lotid',
            headerName: 'LOT ID',
            headerAlign: 'center',
            align: 'center',
            type: 'number',
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
            width: 80
        },
        {
            field: 'roughquality',
            headerName: 'ROUGH QUALITY',
            headerAlign: 'center',
            align: 'center',
            renderCell: renderCellExpand,
            width: 160
        },
        {
            field: 'roughsize',
            headerName: 'ROUGH SIZE',
            headerAlign: 'center',
            align: 'center',
            renderCell: renderCellExpand,
            width: 160
        },
        {
            field: 'pcs',
            headerName: 'PCS',
            headerAlign: 'center',
            align: 'center',
            type: 'number',
            renderCell: renderCellExpand,
            width: 80
        },
        {
            field: 'carat',
            headerName: 'CARAT',
            headerAlign: 'center',
            align: 'center',
            type: 'number',
            renderCell: renderCellExpand,
            width: 80
        },
        {
            field: 'avgsize',
            headerName: 'AVG. SIZE',
            headerAlign: 'center',
            align: 'center',
            type: 'number',
            renderCell: renderCellExpand,
            width: 80
        },
        {
            field: 'grossprice',
            headerName: 'GROSS PRICE',
            headerAlign: 'center',
            align: 'center',
            type: 'number',
            renderCell: renderCellExpand,
            width: 130
        },
        {
            field: 'grosstotal',
            headerName: 'GROSS TOTAL',
            headerAlign: 'center',
            align: 'center',
            type: 'number',
            renderCell: renderCellExpand,
            width: 130
        },
        {
            field: 'chargeprice',
            headerName: 'CHARGE PRICE',
            headerAlign: 'center',
            align: 'center',
            type: 'number',
            renderCell: renderCellExpand,
            width: 130
        },
        {
            field: 'price',
            headerName: 'PRICE',
            headerAlign: 'center',
            align: 'center',
            type: 'number',
            renderCell: renderCellExpand,
            width: 130
        },
        {
            field: 'total',
            headerName: 'TOTAL',
            headerAlign: 'center',
            align: 'center',
            type: 'number',
            renderCell: renderCellExpand,
            width: 130
        },
        {
            field: 'remark',
            headerName: 'REMARK',
            headerAlign: 'center',
            align: 'center',
            renderCell: renderCellExpand,
            width: 230
        },
    ]
    const columns = initialColumns();

    return (
        <Box>
            <Modal
                backdrop="static"
                show={true}
                onHide={handleClose}
                dialogClassName="bhb"
                enforceFocus={false}
            >

                <CommonSnackbar
                    show={showSnackbar}
                    message={snackbarMessage}
                    variant={snackbarVariant}
                    autoHide={Constants.SnackBarCloseTimer}
                    title={snackbarVariant === "success" ? "Success" :
                        snackbarVariant === "error" ? "Error" : "Warning"
                    }
                    handleClose={() => setShowSnackbar(false)}
                />

                <Modal.Header closeButton>
                    <Modal.Title>
                        <img
                            src={logo}
                            height="40pt"
                            width="40pt"
                            alt={Constants.COMPANY_SITE}
                        />
                        &nbsp;&nbsp;Split Lot
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>

                    {/* DataGridPro */}
                    <Box
                        sx={{
                            width: "100%",
                            backgroundColor: '#fff',
                            color: '#000',
                            position: "relative",
                            paddingTop: 1,
                            "& .MuiTablePagination-selectLabel": {
                                marginTop: "15px",
                            },
                            "& .MuiTablePagination-displayedRows": {
                                marginTop: "15px",
                            },
                            "& .css-1jbbcbn-MuiDataGrid-columnHeaderTitle": {
                                fontWeight: Constants.defaultFontWeight,
                            },
                            "& .css-cc8tf1": {
                                fontWeight: Constants.defaultFontWeight,
                            },
                            height: "70vh",
                        }}
                    >
                        <DataGridPro
                            disableRowSelectionOnClick={true}
                            sx={{
                                '& .MuiDataGrid-columnHeader': {
                                    color: '#fff',
                                    backgroundColor: 'grey',
                                },
                                "&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell": {
                                    py: "4px",
                                },
                                "&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell": {
                                    py: "12px",
                                },
                                "&.MuiDataGrid-root--densityComfortable .MuiDataGrid-cell": {
                                    py: "18px",
                                },

                                fontSize: Constants.defaultTableFontSize, fontWeight: Constants.defaultFontWeight,
                            }}
                            density="compact"
                            loading={uploadLoader}
                            slots={{ toolbar: GridToolbar }}
                            rows={data}
                            columns={columns}
                            getRowId={(row) => row.id}
                            initialState={{
                                columns: {
                                    columnVisibilityModel: {
                                        // Hide columns id, the other columns will remain visible
                                        createdby: false,
                                        createdbyusername: false,
                                        lockedby: false,
                                        status: false,
                                        rapnetdate: false,
                                        createdat: false
                                    },
                                },
                                pagination: {
                                    paginationModel: Constants.pageSize,
                                },
                                sorting: {
                                    sortModel: [{ field: "updatedat", sort: "desc" }],
                                },
                            }}
                            pageSizeOptions={Constants.pageSizeOptions}
                            pagination
                            slotProps={{
                                toolbar: {
                                    showQuickFilter: true,
                                    quickFilterProps: { debounceMs: 500 },
                                },
                            }}
                        />

                        <Box
                            style={{
                                position: "absolute",
                                top: "16px",
                                right: "35rem",
                                display: "flex",
                                alignItems: "center",
                                gap: "5px",
                            }}
                        >
                            <p style={{ margin: 0 }}>
                                <span>Lot Carat:</span> <b>{rowData.pendingcarats}</b> &nbsp;&nbsp;|&nbsp;&nbsp;
                                <span>Lot Pcs:</span> <b>{rowData.pendingpcs}</b> &nbsp;&nbsp;|&nbsp;&nbsp;
                                <span>Total Carat:</span> <b>{totalCarat}</b> &nbsp;&nbsp;|&nbsp;&nbsp;
                                <span>Total Pcs: </span> <b>{totalPcs}</b></p>
                        </Box>

                        <Box
                            style={{
                                position: "absolute",
                                top: "14px",
                                right: "260px",
                                display: "flex",
                                alignItems: "center",
                                gap: "5px",
                            }}
                        >

                            <Tooltip title="Download Sample Packet Upload">
                                <a
                                    href="/demo_excel/SampleSplitLotInMultipleLotUpload.csv"
                                    download="SampleSplitLotInMultipleLotUpload.csv"
                                    style={{ color: "red", marginRight: "5px", marginLeft: "5px" }}
                                >
                                    <BackupTable size={25} />
                                </a>
                            </Tooltip>

                            <Tooltip title="Upload Packet">
                                <LoadingButton
                                    color="info"
                                    variant="outlined"
                                    size="small"
                                    sx={{ ml: "3px" }}
                                    component="label"
                                    loading={uploadLoader}
                                    loadingPosition="start"
                                    startIcon={<UploadFileIcon size="large" />}
                                >
                                    Upload
                                    <input
                                        id="file-input"
                                        hidden
                                        accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
                                        multiple
                                        type="file"
                                        onChange={(e) => handleUpload(e.target.files[0])}
                                        onClick={(e) => handleClear()}
                                    />
                                </LoadingButton>
                            </Tooltip>

                            {uploadLoader ? (
                                <CircularProgress size="1rem" />
                            ) : (
                                <Tooltip title="Reload List">
                                    <RefreshOutlinedIcon
                                        onClick={() => setData([])}
                                        color="primary"
                                        fontSize="medium"
                                        sx={{ cursor: "pointer" }}
                                    />
                                </Tooltip>
                            )}
                        </Box>
                    </Box>

                </Modal.Body>
                <Modal.Footer>
                    <LoadingButton
                        variant="contained"
                        onClick={() => splitLotIntoMultiple()}
                        loading={loading}
                        loadingPosition="start"
                        startIcon={<MdCallSplit />}
                        sx={{ mr: "10px" }}
                    >
                        Split Lot
                    </LoadingButton>
                    <Button variant="contained" color="error" onClick={handleClose}>
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>
        </Box>
    );
}

export default LotSplitModal;